import * as THREE from 'three';
import { NODE_DISK } from './createNeuralGraphLayer/constants';
import { HALO_FRAG, NODE_FRAG } from './neuralMaterials/nodeLooks';

const START_CAPACITY = 64;
const HALO_PLANE = new THREE.PlaneGeometry(2, 2);

const _obj = new THREE.Object3D();
const _fwd = new THREE.Vector3();
const _cam = new THREE.Vector3();
const diskList = [];
const haloList = [];

/**
 * Camera-facing quad. instanceMatrix carries translation and scale only —
 * the view matrix supplies screen right and up, so the CPU never copies
 * a quaternion onto each mission.
 */
const BILLBOARD_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vRing;
  varying vec3 vFill;
  varying float vHollow;
  varying float vOpacity;
  attribute vec3 aRing;
  attribute vec3 aFill;
  attribute float aHollow;
  attribute float aOpacity;
  void main() {
    vUv = uv;
    vRing = aRing;
    vFill = aFill;
    vHollow = aHollow;
    vOpacity = aOpacity;
    vec3 center = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    float s = length((modelMatrix * instanceMatrix * vec4(1.0, 0.0, 0.0, 0.0)).xyz);
    vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
    vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
    vec3 pos = center + (right * position.x + up * position.y) * s;
    gl_Position = projectionMatrix * viewMatrix * vec4(pos, 1.0);
  }
`;

const HALO_BILLBOARD_VERT = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vColor;
  varying float vOpacity;
  varying float vPulse;
  varying float vElectrons;
  varying float vInner;
  attribute vec3 aColor;
  attribute float aOpacity;
  attribute float aPulse;
  attribute float aElectrons;
  attribute float aInner;
  void main() {
    vUv = uv;
    vColor = aColor;
    vOpacity = aOpacity;
    vPulse = aPulse;
    vElectrons = aElectrons;
    vInner = aInner;
    vec3 center = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    float s = length((modelMatrix * instanceMatrix * vec4(1.0, 0.0, 0.0, 0.0)).xyz);
    vec3 right = vec3(viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0]);
    vec3 up = vec3(viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1]);
    vec3 pos = center + (right * position.x + up * position.y) * s;
    gl_Position = projectionMatrix * viewMatrix * vec4(pos, 1.0);
  }
`;

function instancedAttr(capacity, itemSize) {
  const attr = new THREE.InstancedBufferAttribute(new Float32Array(capacity * itemSize), itemSize);
  attr.setUsage(THREE.DynamicDrawUsage);
  return attr;
}

function writeMatrix(inst, index, position, scale) {
  _obj.position.copy(position);
  _obj.quaternion.identity();
  _obj.scale.setScalar(Math.max(scale, 0.0001));
  _obj.updateMatrix();
  inst.setMatrixAt(index, _obj.matrix);
}

function viewDepth(position) {
  const p = position;
  return (p.x - _cam.x) * _fwd.x + (p.y - _cam.y) * _fwd.y + (p.z - _cam.z) * _fwd.z;
}

function makeDisks(capacity) {
  const geom = NODE_DISK.clone();
  geom.setAttribute('aRing', instancedAttr(capacity, 3));
  geom.setAttribute('aFill', instancedAttr(capacity, 3));
  geom.setAttribute('aHollow', instancedAttr(capacity, 1));
  geom.setAttribute('aOpacity', instancedAttr(capacity, 1));
  const mat = new THREE.ShaderMaterial({
    vertexShader: BILLBOARD_VERT,
    fragmentShader: NODE_FRAG,
    transparent: true,
    depthWrite: true,
    fog: false,
    toneMapped: false,
  });
  const mesh = new THREE.InstancedMesh(geom, mat, capacity);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.renderOrder = 2;
  mesh.raycast = () => {};
  mesh.count = 0;
  return mesh;
}

function makeHalos(capacity) {
  const geom = HALO_PLANE.clone();
  geom.setAttribute('aColor', instancedAttr(capacity, 3));
  geom.setAttribute('aOpacity', instancedAttr(capacity, 1));
  geom.setAttribute('aPulse', instancedAttr(capacity, 1));
  geom.setAttribute('aElectrons', instancedAttr(capacity, 1));
  geom.setAttribute('aInner', instancedAttr(capacity, 1));
  const mat = new THREE.ShaderMaterial({
    uniforms: {
      uSharp: { value: 0.28 },
      uTime: { value: 0 },
    },
    vertexShader: HALO_BILLBOARD_VERT,
    fragmentShader: HALO_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
    fog: false,
  });
  const mesh = new THREE.InstancedMesh(geom, mat, capacity);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.renderOrder = 1;
  mesh.raycast = () => {};
  mesh.count = 0;
  return mesh;
}

function disposeMesh(mesh) {
  if (!mesh) return;
  mesh.parent?.remove(mesh);
  mesh.geometry.dispose();
  mesh.material.dispose();
}

/** One draw for every mission disk, one draw for every halo. */
export function createNodeInstanceLayer(nodeGroup) {
  let disks = null;
  let halos = null;

  function ensure(which, needed) {
    const current = which === 'disks' ? disks : halos;
    if (current && needed <= current.instanceMatrix.count) return current;
    let cap = current ? current.instanceMatrix.count : START_CAPACITY;
    while (cap < needed) cap *= 2;
    disposeMesh(current);
    const next = which === 'disks' ? makeDisks(cap) : makeHalos(cap);
    nodeGroup.add(next);
    if (which === 'disks') disks = next;
    else halos = next;
    return next;
  }

  function syncDisks(meshByTaskId, camera) {
    diskList.length = 0;
    meshByTaskId.forEach((mesh) => {
      const op = mesh.material?.uniforms?.uOpacity?.value ?? 0;
      if (!mesh.visible || op < 0.004) return;
      diskList.push(mesh);
    });
    if (!diskList.length) {
      if (disks) disks.count = 0;
      return;
    }
    if (camera) {
      camera.getWorldDirection(_fwd);
      _cam.copy(camera.position);
      diskList.sort((a, b) => viewDepth(b.position) - viewDepth(a.position));
    }
    const inst = ensure('disks', diskList.length);
    const ring = inst.geometry.getAttribute('aRing');
    const fill = inst.geometry.getAttribute('aFill');
    const hollow = inst.geometry.getAttribute('aHollow');
    const opacity = inst.geometry.getAttribute('aOpacity');
    for (let i = 0; i < diskList.length; i += 1) {
      const mesh = diskList[i];
      const u = mesh.material.uniforms;
      ring.setXYZ(i, u.uRing.value.r, u.uRing.value.g, u.uRing.value.b);
      fill.setXYZ(i, u.uFill.value.r, u.uFill.value.g, u.uFill.value.b);
      hollow.setX(i, u.uHollow.value);
      opacity.setX(i, u.uOpacity.value);
      writeMatrix(inst, i, mesh.position, mesh.scale.x);
    }
    ring.needsUpdate = true;
    fill.needsUpdate = true;
    hollow.needsUpdate = true;
    opacity.needsUpdate = true;
    inst.instanceMatrix.needsUpdate = true;
    inst.count = diskList.length;
  }

  function syncHalos(haloByTaskId, pulseT) {
    haloList.length = 0;
    haloByTaskId.forEach(({ halo }) => {
      const op = halo.material?.uniforms?.uOpacity?.value ?? 0;
      if (!halo.visible || op < 0.01) return;
      haloList.push(halo);
    });
    if (!haloList.length) {
      if (halos) halos.count = 0;
      return;
    }
    const inst = ensure('halos', haloList.length);
    inst.material.uniforms.uTime.value = pulseT || 0;
    const color = inst.geometry.getAttribute('aColor');
    const opacity = inst.geometry.getAttribute('aOpacity');
    const pulse = inst.geometry.getAttribute('aPulse');
    const electrons = inst.geometry.getAttribute('aElectrons');
    const inner = inst.geometry.getAttribute('aInner');
    for (let i = 0; i < haloList.length; i += 1) {
      const halo = haloList[i];
      const u = halo.material.uniforms;
      color.setXYZ(i, u.uColor.value.r, u.uColor.value.g, u.uColor.value.b);
      opacity.setX(i, u.uOpacity.value);
      pulse.setX(i, u.uPulse.value);
      electrons.setX(i, u.uElectrons.value);
      inner.setX(i, u.uInner.value);
      writeMatrix(inst, i, halo.position, halo.scale.x);
    }
    color.needsUpdate = true;
    opacity.needsUpdate = true;
    pulse.needsUpdate = true;
    electrons.needsUpdate = true;
    inner.needsUpdate = true;
    inst.instanceMatrix.needsUpdate = true;
    inst.count = haloList.length;
  }

  return {
    sync(meshByTaskId, haloByTaskId, camera, pulseT) {
      syncDisks(meshByTaskId, camera);
      syncHalos(haloByTaskId, pulseT);
    },
    dispose() {
      disposeMesh(disks);
      disposeMesh(halos);
      disks = null;
      halos = null;
    },
    get disks() { return disks; },
    get halos() { return halos; },
  };
}
